import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Scroll Restoration Component - preserves scroll position in dashboard
const ScrollManager = () => {
  const location = useLocation();
  
  React.useEffect(() => {
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Only scroll to top for non-dashboard routes (public pages)
    if (!location.pathname.startsWith('/dashboard')) {
      window.scrollTo(0, 0);
    }
    // For dashboard routes, do nothing - keep current scroll position
  }, [location.pathname]);
  
  return null;
};

// Public Site - Using PreviewPage for full content fidelity
import PreviewPage from './pages/PreviewPage';

// Gift Card Request Page
import GiftCardRequestPage from './pages/GiftCardRequestPage';

// Auth
import LoginPage from './pages/auth/LoginPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Dashboard
import DashboardLayout from './layouts/DashboardLayout';
import DashboardPage from './pages/dashboard/DashboardPage';
import ReservationsPage from './pages/dashboard/ReservationsPage';
import PlanningPage from './pages/dashboard/PlanningPage';
import MediaLibraryPage from './pages/dashboard/MediaLibraryPage';
import ProductsManagementPage from './pages/dashboard/ProductsManagementPage';
import GiftCardsPage from './pages/dashboard/GiftCardsPage';
import ContractsPage from './pages/dashboard/ContractsPage';
import CustomersPage from './pages/dashboard/CustomersPage';
import SiteSettingsPage from './pages/dashboard/SiteSettingsPage';
import PageEditorPage from './pages/dashboard/PageEditorPage';
import SEOUnifiedPage from './pages/dashboard/SEOUnifiedPage';
import BlogManagementPage from './pages/dashboard/BlogManagementPage';
import SettingsPage from './pages/dashboard/SettingsPage';
import GoogleReviewsPage from './pages/dashboard/GoogleReviewsPage';
import DeliveryConfigPage from './pages/dashboard/DeliveryConfigPage';
import HotspotsConfigPage from './pages/dashboard/HotspotsConfigPage';
import EmailTemplatesPage from './pages/dashboard/EmailTemplatesPage';
import PartnerLogosPage from './pages/dashboard/PartnerLogosPage';
import PaymentSettingsPage from './pages/dashboard/PaymentSettingsPage';
import BacklogPage from './pages/dashboard/BacklogPage';
import EquipmentPage from './pages/dashboard/EquipmentPage';
import DesignsListPage from './pages/dashboard/DesignsListPage';
import DesignApprovalPage from './pages/dashboard/DesignApprovalPage';
import ContractTemplatePage from './pages/dashboard/ContractTemplatePage';
import TeamPage from './pages/dashboard/TeamPage';
import CollaboratorsPage from './pages/dashboard/CollaboratorsPage';
import MyPOSSettingsPage from './pages/dashboard/MyPOSSettingsPage';
import SitemapSettingsPage from './pages/dashboard/SitemapSettingsPage';
import DataExportPage from './pages/dashboard/DataExportPage';
import PaymentMethodsPage from './pages/dashboard/PaymentMethodsPage';
import EmailConfigPage from './pages/dashboard/EmailConfigPage';
import ProductsFormulasPage from './pages/dashboard/ProductsFormulasPage';
import InvoicesPage from './pages/dashboard/InvoicesPage';
import ReservationDetailPage from './pages/dashboard/ReservationDetailPage';
import PromoCodesPage from './pages/dashboard/PromoCodesPage';
import CustomerDetailPage from './pages/dashboard/CustomerDetailPage';
import EmailLogsPage from './pages/dashboard/EmailLogsPage';
import ConstructorPage from './pages/dashboard/ConstructorPage';
import ContentManagerPage from './pages/dashboard/ContentManagerPage';
import GiftCardRequestsPage from './pages/dashboard/GiftCardRequestsPage';

// Client Portal
import ClientPortalPage from './pages/ClientPortalPage';

// Payment Pages
import { PaymentSuccess, PaymentCancel } from './pages/PaymentPages';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#d4af37] border-t-transparent mx-auto mb-4" />
          <p className="text-white/60">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  // Check role if required
  if (requiredRoles.length > 0 && user) {
    const userRole = user.role || (user.is_super_admin ? 'super_admin' : 'entreprise');
    // Admin role has access to everything entreprise and super_admin have
    const effectiveRoles = userRole === 'admin' ? ['admin', 'entreprise', 'super_admin'] : [userRole];
    const hasAccess = requiredRoles.some(role => effectiveRoles.includes(role));
    if (!hasAccess) {
      // Redirect clients to their portal, others to dashboard
      if (userRole === 'client') {
        return <Navigate to="/mon-compte" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  return children;
};

// Super Admin Route
const SuperAdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#d4af37] border-t-transparent" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  if (!user?.is_super_admin && user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <>
      <ScrollManager />
      <Routes>
      {/* ========== PUBLIC SITE - JUST A SMILE EVENTS ========== */}
      {/* Use PreviewPage with standalone mode for full content fidelity */}
      <Route path="/" element={<PreviewPage standaloneMode={true} />} />
      <Route path="/carte-cadeau" element={<GiftCardRequestPage />} />
      <Route path="/blog/:articleSlug" element={<PreviewPage standaloneMode={true} />} />
      <Route path="/seo/:seoSlug" element={<PreviewPage standaloneMode={true} />} />
      <Route path="/:pageSlug" element={<PreviewPage standaloneMode={true} />} />
      
      {/* ========== ADMIN LOGIN ========== */}
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/login" element={<Navigate to="/admin/login" replace />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* ========== CLIENT PORTAL (Mon Compte) ========== */}
      <Route path="/mon-compte" element={
        <ProtectedRoute requiredRoles={['client']}>
          <ClientPortalPage />
        </ProtectedRoute>
      } />
      
      {/* ========== DASHBOARD - ENTREPRISE & SUPER ADMIN ========== */}
      <Route path="/dashboard" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin', 'collaborator']}>
          <DashboardLayout>
            <DashboardPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/reservations" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin', 'collaborator']}>
          <DashboardLayout>
            <ReservationsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/reservations/:id" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin', 'collaborator']}>
          <DashboardLayout>
            <ReservationDetailPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/planning" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin', 'collaborator']}>
          <DashboardLayout>
            <PlanningPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/media" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <MediaLibraryPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/products" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <ProductsManagementPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/gift-cards" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <GiftCardsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/gift-card-requests" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <GiftCardRequestsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/contracts" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <ContractsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/contracts/template" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <ContractTemplatePage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/designs" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <DesignsListPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/design/:reservationId" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <DesignApprovalPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/team" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <TeamPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/collaborators" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin', 'admin']}>
          <DashboardLayout>
            <CollaboratorsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/customers" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <CustomersPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/customers/new" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <CustomerDetailPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/customers/:customerId" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <CustomerDetailPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* Constructor - New Visual Page Editor */}
      <Route path="/dashboard/constructor" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <ConstructorPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/constructor/:pageSlug" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <ConstructorPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* Content Manager - Logos, Models, Realizations */}
      <Route path="/dashboard/content" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <ContentManagerPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* Page Editor - Unified Elementor-style editor */}
      <Route path="/dashboard/site-editor" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <PageEditorPage />
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/pages" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <PageEditorPage />
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/pages/:pageSlug" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <PageEditorPage />
        </ProtectedRoute>
      } />
      
      {/* Route seo-pages redirige vers la nouvelle page unifiée */}
      <Route path="/dashboard/seo-pages" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <SEOUnifiedPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/blog" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <BlogManagementPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/google-reviews" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <GoogleReviewsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/paiements" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <PaymentSettingsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/backlog" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <BacklogPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/equipment" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <EquipmentPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/products-formulas" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <ProductsFormulasPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/invoices" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <InvoicesPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/settings" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <SettingsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/delivery-config" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <DeliveryConfigPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/site-settings" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <SiteSettingsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/hotspots" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <HotspotsConfigPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/email-templates" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <EmailTemplatesPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/partner-logos" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <PartnerLogosPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/mypos-settings" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <MyPOSSettingsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/sitemap" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <SitemapSettingsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/export" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <DataExportPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/seo" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <SEOUnifiedPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/email-logs" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <EmailLogsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/payment-methods" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <PaymentMethodsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/promo-codes" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <PromoCodesPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/email-config" element={
        <ProtectedRoute requiredRoles={['entreprise', 'super_admin']}>
          <DashboardLayout>
            <EmailConfigPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* ========== SUPER ADMIN ONLY ========== */}
      <Route path="/dashboard/admin/*" element={
        <SuperAdminRoute>
          <DashboardLayout>
            <DashboardPage />
          </DashboardLayout>
        </SuperAdminRoute>
      } />
      
      {/* ========== PAYMENT PAGES ========== */}
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      
      {/* ========== CATCH ALL - REDIRECT TO HOME ========== */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              style: {
                background: '#1a1a2e',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                color: '#fff',
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
